import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { getBoardColumnApi } from '../../../services/api';

/**
 * Fetches full paginated events for a single Kanban column.
 *
 * Props:
 *   stageKey  — one of: production | editing | internal_revision | client_revision | completed
 *   orgId     — organisation id (omitted for Editor role)
 *   enabled   — boolean; starts as false, set to true when the user expands the column
 *
 * Returns:
 *   events            — flat array of all fetched events for this column
 *   hasNextPage, isFetchingNextPage, fetchNextPage
 *   isLoading, isError
 */
export const useKanbanColumnData = ({ stageKey, orgId, name, editorId, eventTypeId, dueWithinHours, quickTurn, hidden, enabled = false } = {}) => {
  const query = useInfiniteQuery(
    ['kanban-column', stageKey, orgId, name, editorId, eventTypeId, dueWithinHours, quickTurn, hidden],
    async ({ pageParam = 1 }) => {
      const params = {
        stage: stageKey,
        page: pageParam,
        ...(orgId ? { organisation_id: orgId } : {}),
        ...(name ? { name } : {}),
        ...(editorId && editorId !== 'unassigned' ? { internal_user_id: editorId } : {}),
        ...(editorId === 'unassigned' ? { unassigned: true } : {}),
        ...(eventTypeId ? { event_type_id: eventTypeId } : {}),
        ...(dueWithinHours && dueWithinHours !== 'overdue' ? { due_within_hours: dueWithinHours } : {}),
        ...(dueWithinHours === 'overdue' ? { overdue: true } : {}),
        ...(quickTurn ? { quick_turn: true } : {}),
        // Admin-only; omitted when off (see useKanbanBoardSummary).
        ...(hidden ? { hidden: true } : {}),
      };
      return getBoardColumnApi(params);
    },
    {
      enabled: enabled && !!stageKey,
      getNextPageParam: (lastPage) =>
        lastPage?.data?.data?.next_page || undefined,
      keepPreviousData: true,
    }
  );

  const events = useMemo(() => {
    const pages = query.data?.pages ?? [];
    return pages.flatMap((page) => page?.data?.data?.events ?? []);
  }, [query.data]);

  return { ...query, events };
};
